// MACHINERY V2 (Stroje) — UI state slice (M2.0 foundation).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §2.3.
//
// Owns ONLY v2 UI state. Every data slice is REUSED UNCHANGED
// (customerMachinery, machineCatalog, customerServiceReports,
// customerMachine, customerMachineDocuments, machineGroups) — that reuse is
// what preserves data parity, so nothing here duplicates or mirrors machine
// data, and no existing slice's shape is touched.
//
// Persistence mirrors midasV3Slice / settingsV2UiSlice: the palette and the
// filter-rail collapse are UI preferences persisted to localStorage (non-auth
// keys, read at module scope — no network, no auth state). The transient
// view/filter/period selections are deliberately NOT persisted; they reset per
// session like the live machinery grid's filters do.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// ============================================================
// Types
// ============================================================

/** Active palette — rendered as the root class `p-claude` | `p-machineid`. */
export type MachineryV2Palette = "claude" | "machineid";

/** Fleet view size. V1 = big cards (3-up), V2 = grouped rows. The dropped
 *  third "compact" card variant (§9 D4) has no representation here on purpose. */
export type MachineryV2FleetView = "V1" | "V2";

/** Live signal-ledger period pills (§8 M2.10):
 *  hour = Posledná hodina · shift = Táto zmena · day = Dnes ·
 *  week = Tento týždeň · total = Celkovo. Each is a REAL rollup window —
 *  never a scaled projection of another window. */
export type MachineryV2LedgerPeriod =
  | "hour"
  | "shift"
  | "day"
  | "week"
  | "total";

interface MachineryV2UiState {
  /** Active palette class suffix — `.p-claude` (default) or `.p-machineid`. */
  palette: MachineryV2Palette;
  /** Fleet view toggle (V1 big cards | V2 grouped rows). */
  fleetView: MachineryV2FleetView;
  /** Selected fleet status filters. Empty array = no status filter (show all).
   *  Typed as string[] rather than the 9-state union so this slice carries no
   *  forward dependency on M2.1's `fleet/cardStates.ts`; M2.1 narrows the
   *  callers, not the store. */
  fleetStatusFilters: string[];
  /** Machine-type filter (machine type id) — null = all types. */
  fleetTypeFilter: string | null;
  /** Location filter (location id) — null = all locations. Carried feature
   *  per §9 D4. */
  fleetLocationFilter: string | null;
  /** Live ledger period pill selection. */
  ledgerPeriod: MachineryV2LedgerPeriod;
  /** Filter-rail collapsed state (fleet + catalog rails share it). */
  railCollapsed: boolean;
}

// ============================================================
// localStorage hydration (mirrors settingsV2UiSlice.ts lines 53-80)
// ============================================================

const LS_PALETTE_KEY = "machinery-v2-palette";
const LS_RAIL_COLLAPSED_KEY = "machinery-v2-rail-collapsed";

const safeLocalStorage = (): Storage | null => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

const readPalette = (): MachineryV2Palette => {
  const ls = safeLocalStorage();
  if (!ls) return "claude";
  const raw = ls.getItem(LS_PALETTE_KEY);
  if (raw === "claude" || raw === "machineid") return raw;
  return "claude";
};

const writePalette = (palette: MachineryV2Palette): void => {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(LS_PALETTE_KEY, palette);
  } catch {
    // localStorage may throw in private mode / on quota; not fatal for v2 UX.
  }
};

const readRailCollapsed = (): boolean => {
  const ls = safeLocalStorage();
  if (!ls) return false;
  return ls.getItem(LS_RAIL_COLLAPSED_KEY) === "true";
};

const writeRailCollapsed = (collapsed: boolean): void => {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(LS_RAIL_COLLAPSED_KEY, String(collapsed));
  } catch {
    // Same rationale as writePalette.
  }
};

// ============================================================
// Slice
// ============================================================

const initialState: MachineryV2UiState = {
  palette: readPalette(),
  fleetView: "V1",
  fleetStatusFilters: [],
  fleetTypeFilter: null,
  fleetLocationFilter: null,
  ledgerPeriod: "hour",
  railCollapsed: readRailCollapsed(),
};

const machineryV2UiSlice = createSlice({
  name: "machineryV2Ui",
  initialState,
  reducers: {
    setPalette: (state, action: PayloadAction<MachineryV2Palette>) => {
      state.palette = action.payload;
      writePalette(action.payload);
    },
    togglePalette: (state) => {
      const next: MachineryV2Palette =
        state.palette === "claude" ? "machineid" : "claude";
      state.palette = next;
      writePalette(next);
    },
    setFleetView: (state, action: PayloadAction<MachineryV2FleetView>) => {
      state.fleetView = action.payload;
    },
    setFleetStatusFilters: (state, action: PayloadAction<string[]>) => {
      state.fleetStatusFilters = action.payload;
    },
    toggleFleetStatusFilter: (state, action: PayloadAction<string>) => {
      const code = action.payload;
      state.fleetStatusFilters = state.fleetStatusFilters.includes(code)
        ? state.fleetStatusFilters.filter((c) => c !== code)
        : [...state.fleetStatusFilters, code];
    },
    setFleetTypeFilter: (state, action: PayloadAction<string | null>) => {
      state.fleetTypeFilter = action.payload;
    },
    setFleetLocationFilter: (state, action: PayloadAction<string | null>) => {
      state.fleetLocationFilter = action.payload;
    },
    clearFleetFilters: (state) => {
      state.fleetStatusFilters = [];
      state.fleetTypeFilter = null;
      state.fleetLocationFilter = null;
    },
    setLedgerPeriod: (
      state,
      action: PayloadAction<MachineryV2LedgerPeriod>
    ) => {
      state.ledgerPeriod = action.payload;
    },
    setRailCollapsed: (state, action: PayloadAction<boolean>) => {
      state.railCollapsed = action.payload;
      writeRailCollapsed(action.payload);
    },
    toggleRailCollapsed: (state) => {
      state.railCollapsed = !state.railCollapsed;
      writeRailCollapsed(state.railCollapsed);
    },
  },
});

export const {
  setPalette,
  togglePalette,
  setFleetView,
  setFleetStatusFilters,
  toggleFleetStatusFilter,
  setFleetTypeFilter,
  setFleetLocationFilter,
  clearFleetFilters,
  setLedgerPeriod,
  setRailCollapsed,
  toggleRailCollapsed,
} = machineryV2UiSlice.actions;

export default machineryV2UiSlice.reducer;
